import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { staffPinApi, teacherPinApi } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { Bus, ForkKnife, GraduationCap, QrCode } from '@phosphor-icons/react';
import QrScanner from '../../components/mobile/QrScanner';

/**
 * Mobile login (PIN) — Cantine / Transport (4 digits) + Teacher (6 digits).
 * Manual: staff/teacher types their ID + PIN.
 * QR-pair: scans a QR encoding `pinlogin:<id>:<pin>` OR `teacherpin:<id>:<pin>`.
 */
const ROLES = [
  { key: 'staff', label: 'Cantine / Transport / Éducateur', icon: ForkKnife, color: '#10B981', pinLen: 4 },
  { key: 'teacher', label: 'Enseignant', icon: GraduationCap, color: '#0052FF', pinLen: 6 },
];

const MobileLogin = () => {
  const [profile, setProfile] = useState('teacher'); // default focus on new feature
  const [id, setId] = useState('');
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const navigate = useNavigate();
  const { checkAuth, user } = useAuth();

  const pinLen = ROLES.find(r => r.key === profile)?.pinLen || 4;

  useEffect(() => {
    if (user?.role === 'transport_staff') navigate('/m/driver', { replace: true });
    else if (user?.role === 'cantine_staff') navigate('/m/cantine', { replace: true });
    else if (user?.role === 'teacher_user') navigate('/m/teacher', { replace: true });
  }, [user, navigate]);

  const routeAfterLogin = (role) => {
    if (role === 'transport_staff') navigate('/m/driver', { replace: true });
    else if (role === 'cantine_staff') navigate('/m/cantine', { replace: true });
    else if (role === 'teacher_user') navigate('/m/teacher', { replace: true });
    else navigate('/', { replace: true });
  };

  const doLogin = useCallback(async (kind, sid, p) => {
    if (!sid || p.length !== (kind === 'teacher' ? 6 : 4)) {
      toast.error('ID et PIN requis');
      return;
    }
    setLoading(true);
    try {
      const { data } = kind === 'teacher'
        ? await teacherPinApi.login({ teacher_id: sid, pin: p })
        : await staffPinApi.login({ staff_id: sid, pin: p });
      await checkAuth();
      toast.success(`Bienvenue ${data.user.name || ''}`);
      routeAfterLogin(data.user.role);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Identifiants invalides');
    } finally { setLoading(false); }
   
  }, [checkAuth, navigate]);

  const submit = (e) => {
    e?.preventDefault();
    doLogin(profile, id.trim(), pin);
  };

  const onScan = (decoded) => {
    setScanning(false);
    const raw = (decoded || '').trim();
    // Accepted formats: pinlogin:<staff_id>:<pin>  OR  teacherpin:<teacher_id>:<pin>
    const mS = /^pinlogin:([^:]+):(\d{4})$/.exec(raw);
    const mT = /^teacherpin:([^:]+):(\d{6})$/.exec(raw);
    if (mT) {
      setProfile('teacher');
      setId(mT[1]); setPin(mT[2]);
      doLogin('teacher', mT[1], mT[2]);
      return;
    }
    if (mS) {
      setProfile('staff');
      setId(mS[1]); setPin(mS[2]);
      doLogin('staff', mS[1], mS[2]);
      return;
    }
    toast.error('QR non reconnu');
  };

  const current = ROLES.find(r => r.key === profile);
  const Icon = current?.icon || GraduationCap;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0052FF] via-[#3B82F6] to-[#10B981] flex items-center justify-center p-4">
      <Card className="w-full max-w-sm border-0 shadow-2xl" data-testid="mobile-login">
        <CardContent className="p-6 space-y-5">
          <div className="text-center">
            <div className="flex justify-center gap-2 mb-3">
              <GraduationCap size={30} className="text-[#0052FF]" weight="duotone" />
              <Bus size={30} className="text-[#3B82F6]" weight="duotone" />
              <ForkKnife size={30} className="text-[#10B981]" weight="duotone" />
            </div>
            <h1 className="font-outfit text-xl font-bold text-[#0F172A]">Connexion mobile</h1>
            <p className="text-sm text-[#64748B] mt-1">
              PIN {pinLen} chiffres — {current?.label}
            </p>
          </div>

          {/* Profile switch */}
          <div className="grid grid-cols-2 gap-2">
            {ROLES.map(r => (
              <button
                key={r.key}
                type="button"
                onClick={() => { setProfile(r.key); setPin(''); }}
                data-testid={`mobile-profile-${r.key}`}
                className={`p-2 rounded-lg border text-sm font-medium transition-all ${
                  profile === r.key
                    ? 'border-[#0052FF] bg-[#EFF6FF] text-[#0052FF]'
                    : 'border-[#E2E8F0] text-[#64748B] hover:bg-[#F8FAFC]'
                }`}
              >
                <r.icon size={18} weight="duotone" className="inline mr-1" />
                {r.key === 'teacher' ? 'Enseignant' : 'Personnel'}
              </button>
            ))}
          </div>

          {scanning ? (
            <QrScanner onScan={onScan} onClose={() => setScanning(false)} />
          ) : (
            <>
              <Button
                type="button"
                variant="outline"
                className="w-full border-[#0052FF] text-[#0052FF] hover:bg-[#EFF6FF]"
                onClick={() => setScanning(true)}
                data-testid="pin-qr-scan-btn"
              >
                <QrCode size={18} className="mr-2" /> Scanner mon QR
              </Button>

              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label className="text-xs font-medium text-[#64748B] uppercase">
                    {profile === 'teacher' ? 'ID Enseignant' : 'ID Personnel'}
                  </label>
                  <Input
                    value={id}
                    onChange={e => setId(e.target.value)}
                    placeholder="ID reçu par l'administration"
                    data-testid="pin-staff-id"
                    autoComplete="off"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-[#64748B] uppercase">
                    Code PIN ({pinLen} chiffres)
                  </label>
                  <Input
                    type="password"
                    inputMode="numeric"
                    pattern={`\\d{${pinLen}}`}
                    maxLength={pinLen}
                    value={pin}
                    onChange={e => setPin(e.target.value.replace(/\D/g, '').slice(0, pinLen))}
                    placeholder={'•'.repeat(pinLen)}
                    className="text-center tracking-[0.5em] text-2xl font-mono"
                    data-testid="pin-input"
                    autoComplete="off"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full"
                  style={{ backgroundColor: current?.color }}
                  disabled={loading}
                  data-testid="pin-submit"
                >
                  <Icon size={16} className="mr-2" weight="duotone" />
                  {loading ? 'Connexion…' : 'Se connecter'}
                </Button>
              </form>
              <p className="text-xs text-center text-[#94A3B8]">
                Votre PIN est fourni par l&apos;administration.
              </p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MobileLogin;
